import * as React from 'react';

import { GROUP_WORDS, type IRuleGroupWords, type IRuleLeafWords, type IRuleWords } from '../../concern-predicate';

/**
 * A concern criterion's rule in words (SR-227, the design's §3.2) — the one rendering of
 * `ruleWords`' output, for the reading view and the form's sentence alike. What an author reads
 * back while writing is what every colleague reads afterwards; two renderers would let the two
 * drift, and the form's sentence would stop being a promise about the page.
 *
 * The words are `concern-predicate.ts`'; this decides only what is bold and how a group nests. A
 * group always draws its line ("all of these are true:"), one condition included, so a second
 * condition added later does not change the shape under the reader. `lead="form"` says the rule
 * as one being written ("This rule will raise an item…"), and only there is a condition
 * unfinished — a stored rule the words could read has every part.
 *
 * `TechnicalDefinition` sits here too, because it is the same rule's other rendering: the body as
 * the system stores it (or as the form would send it), pretty-printed and never editable — the
 * owner's ruling (the design's §9, Q1). It is called a technical definition, never JSON: the link
 * says what it is for, not what format it is in.
 */
export function RuleWords({ words, lead = 'page' }: { words: IRuleWords; lead?: 'page' | 'form' }) {
  const opening = lead === 'form' ? 'This rule will raise an item' : 'Raise an item';
  return (
    <div data-rule-words className="grid gap-1 text-[0.875rem] text-[var(--color-midnight-blue)]">
      <p>
        {opening}{' '}
        {words.atLeast === null ? (
          <>
            for <strong>each disclosure</strong> where
          </>
        ) : (
          <>
            when a person has{' '}
            <strong>
              at least{' '}
              {words.atLeast === '' ? <Unfinished>(a number not filled in yet)</Unfinished> : words.atLeast}
            </strong>{' '}
            live disclosures where
          </>
        )}
      </p>
      <GroupWords group={words.group} />
    </div>
  );
}

function GroupWords({ group }: { group: IRuleGroupWords }) {
  return (
    <>
      <p>
        <strong>{group.op}</strong>{' '}
        {GROUP_WORDS[group.op]}
      </p>
      <ul className="grid list-disc gap-0.5 pl-5">
        {group.items.map((item) =>
          item.type === 'group' ? (
            <li key={item.id} className="grid gap-1">
              <GroupWords group={item} />
            </li>
          ) : (
            <li key={item.id}>
              <LeafWords leaf={item} />
            </li>
          )
        )}
      </ul>
    </>
  );
}

function LeafWords({ leaf }: { leaf: IRuleLeafWords }) {
  if (leaf.unfinished) {
    // What is chosen so far, then the gap — never a sentence that reads as finished.
    const parts = [leaf.field, leaf.operator, leaf.value ?? ''].filter((part) => part !== '');
    return (
      <>
        {parts.length > 0 && <>{parts.join(' ')} </>}
        <Unfinished>(a condition not filled in yet)</Unfinished>
      </>
    );
  }
  return (
    <>
      <span className="font-medium">{leaf.field}</span> {leaf.operator}
      {leaf.value !== null && (
        <>
          {' '}
          <span className="font-medium">{leaf.value}</span>
        </>
      )}
    </>
  );
}

function Unfinished({ children }: { children: React.ReactNode }) {
  return <em className="font-normal text-muted-foreground">{children}</em>;
}

/**
 * The stored form, read-only: closed behind "Show the technical definition" — or `alwaysOpen`, where
 * it is the only rendering of the rule there is (a shape the words cannot say). A plain disclosure
 * button, not a `<details>`: the kit has no Collapsible, and a summary would be a second fold the
 * section memory knows nothing of.
 */
export function TechnicalDefinition({
  value,
  note,
  alwaysOpen = false
}: {
  value: unknown;
  note: string;
  alwaysOpen?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const id = React.useId();
  const shown = alwaysOpen || open;
  return (
    <div className="grid gap-2">
      {!alwaysOpen && (
        <button
          type="button"
          aria-expanded={open}
          aria-controls={id}
          onClick={() => setOpen((was) => !was)}
          className="w-fit text-[0.8125rem] text-[var(--color-sapphire)] underline underline-offset-2 hover:no-underline"
        >
          {open ? 'Hide the technical definition' : 'Show the technical definition'}
        </button>
      )}
      {shown && (
        <div id={id} data-predicate-json className="grid gap-1">
          <p className="text-[0.8125rem] font-medium">Technical definition</p>
          <pre className="overflow-x-auto rounded-md bg-[var(--color-off-white)] p-3 font-mono text-xs whitespace-pre-wrap break-words text-[var(--color-midnight-blue)] [contain:inline-size]">
            {JSON.stringify(value, null, 2)}
          </pre>
          <p className="text-xs text-muted-foreground">{note}</p>
        </div>
      )}
    </div>
  );
}
